import { cloneElement, ReactElement, ReactNode } from 'react'
import { isUndefined } from 'lodash'
import { matchPath } from 'react-router'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import {
  TabBar as UiKitTabBar,
  TabBarProps as UiKitTabBarProps,
} from '@revolut-internal/ui-kit'

import { ActionTab, BehaviourVariant, RouteTab } from './types'

type TabBarProps<T = BehaviourVariant, D extends string = string> = Omit<
  UiKitTabBarProps,
  'value' | 'defaultValue' | 'onChange'
> & {
  behaviourVariant: T
  tabs: T extends 'action' ? ActionTab<D>[] : RouteTab<D>[]
  exact?: boolean
  replace?: boolean
  value?: D
  defaultValue?: D
  renderWrapper?: (tab: ActionTab<D> | RouteTab<D>, children: ReactNode) => ReactElement
  onChange?: (value: D | null) => void
}

export const TabBar = <T extends BehaviourVariant, D extends string = string>({
  tabs,
  defaultValue,
  behaviourVariant,
  exact,
  replace,
  renderWrapper,
  ...props
}: TabBarProps<T, D>) => {
  const location = useLocation()

  return (
    <UiKitTabBar {...props}>
      {tabs.map((tab, index) => {
        const isRouteTab = 'to' in tab
        const isSelected = isRouteTab
          ? Boolean(matchPath(location.pathname, { path: tab.to?.split('?')[0], exact }))
          : undefined

        if (!isUndefined(tab.visible) && !tab.visible) {
          return null
        }

        const tabProps = isRouteTab
          ? {
              'aria-label': tab.ariaLabel,
              disabled: tab.disabled,
              use: RouterLink,
              replace,
              to: tab.to,
              children: tab.label,
            }
          : {
              'aria-label': tab.ariaLabel,
              disabled: tab.disabled,
              to: tab.value,
              children: tab.label,
            }

        const TabComponent = (
          <UiKitTabBar.Item
            key={renderWrapper ? undefined : index}
            {...tabProps}
            aria-selected={isSelected}
          />
        )

        return renderWrapper
          ? cloneElement(renderWrapper(tab, TabComponent), { key: index })
          : TabComponent
      })}
    </UiKitTabBar>
  )
}
